import { useEffect, useRef, useState } from 'react';
import { IonDatetime, IonModal, IonSpinner } from '@ionic/react';
import { CalendarClock, ChevronLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Address } from '../../types/address';
import type { DishListing, FulfillmentMethod } from '../../types/listing';
import { extractTime, withRefDate } from '../../utils/listingTimeWindow';
import { formatPickupMoment, formatTimeRange } from '../../utils/timeWindow';
import { dateOf, getPickupBounds, resolvePickup, timeOf } from '../../utils/pickupTime';
import { formatPriceCents } from '../../utils/currency';
import { useProfileStore } from '../../store/useProfileStore';
import { useLocationStore } from '../../store/useLocationStore';
import { distanceMiles, parseObfuscated } from '../../utils/geoDistance';
import {
  LocationPicker,
  buildProfileLocation,
  type LocationPickerHandle,
  type LocationPickerValue,
} from '../location/LocationPicker';
import { Icons } from '../../services/iconService';

export interface PortionPickerResult {
  quantity: number;
  fulfillmentMethod: FulfillmentMethod;
  /**
   * Naive ISO "YYYY-MM-DDTHH:mm:ss" in the listing's timezone.
   *
   * For pickup this is the time the eater chose to come by. For drop-off they
   * choose nothing — the chef drives one run — so it's the START of that run's
   * window, which is the earliest the food could arrive. Downstream this feeds
   * `saleDate` and `intendedPickupTime`, both of which want "when is this
   * order's handoff" rather than "what did the user tap".
   */
  pickupTime: string;
  /** Drop-off only: where the food is going. */
  address?: Address | null;
}

interface PortionPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: PortionPickerResult) => void;
  listing: DishListing;
  isSubmitting?: boolean;
}

/** Which half of the flow is on screen. */
type Step = 'method' | 'details' | 'address';

export const PortionPickerModal: React.FC<PortionPickerModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  listing,
  isSubmitting = false,
}) => {
  // Bound to `listing`, with `orders` declared so the `orders:` cross-namespace
  // fulfillment labels resolve instead of rendering as raw keys.
  const { t, i18n } = useTranslation(['listing', 'orders', 'common']);
  // 0 when the backend omitted the field (a listing with no quantity). Fail
  // closed: stock we cannot verify should not be orderable.
  const maxQuantity = listing.itemsRemaining ?? 0;
  const methods = listing.fulfillmentMethod ?? [];
  const offersPickup = methods.includes('pickup');
  const offersDropoff = methods.includes('dropoff');

  // Each method is bounded by ITS OWN window. This used to read
  // `listing.timeWindow` — the envelope spanning both — so on a listing
  // offering both, the pickup wheel happily offered times that fell inside the
  // chef's drop-off run. Falls back to the envelope only for listings written
  // before the windows were split.
  const pickupWindow = listing.pickupWindow ?? listing.timeWindow;
  const bounds = pickupWindow ? getPickupBounds(pickupWindow) : null;

  // Seeds only. The chosen address belongs to the ORDER, not the profile:
  // someone can have food sent to work today and a friend's place tomorrow
  // without either becoming the address they're billed at.
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const profileAddress =
    activeProfile?.metadata?.addresses?.find((a) => a.type === 'profile') ?? null;
  const deviceLocation = useLocationStore((s) => s.location);
  const deviceIsPrecise = useLocationStore((s) => s.locationSource === 'device');

  const [step, setStep] = useState<Step>('details');
  const [orderAddress, setOrderAddress] = useState<LocationPickerValue | null>(null);
  // The picker reports these; both must be answered before an address counts
  // as somewhere a chef can actually stop. Same gate the publish form applies
  // to a listing's own location.
  const [aptResolved, setAptResolved] = useState(true);
  const [pinResolved, setPinResolved] = useState(true);
  const locationPickerRef = useRef<LocationPickerHandle>(null);
  const [quantity, setQuantity] = useState(1);
  const [fulfillmentMethod, setFulfillmentMethod] = useState<FulfillmentMethod>('pickup');
  // Full naive "YYYY-MM-DDTHH:mm:ss" key, not a bare time: a window that
  // crosses midnight spans two dates, so which date the picked time lands on is
  // part of the answer (see resolvePickup).
  const [pickupKey, setPickupKey] = useState<string>(bounds?.min ?? '');
  // Buyers were ordering future-dated listings thinking they were for today:
  // this wheel shows a bare clock time, so nothing here ever said which day.
  // A pickup that isn't today now has to be ticked off explicitly before
  // Confirm unlocks.
  const [dateAcknowledged, setDateAcknowledged] = useState(false);

  // Reset whenever the modal opens so a second tap starts clean. A listing with
  // one method skips the chooser entirely — there's nothing to choose, and
  // making everyone tap through a single-option screen would be worse than the
  // combined screen this replaced.
  useEffect(() => {
    if (!isOpen) return;
    setQuantity(1);
    setPickupKey(bounds?.min ?? '');
    setDateAcknowledged(false);
    // Suggested ONLY from a real current-location fix. No profile fallback:
    // the profile address is a billing/tax value that is often just a ZIP, and
    // offering it as a delivery destination invites someone to accept an
    // address that was never meant to be one. With no fix we start empty and
    // they enter it — every time. `autoSeed={false}` on the picker below stops
    // it reaching for the browser behind that decision.
    setOrderAddress(deviceIsPrecise ? buildProfileLocation(deviceLocation ?? null) : null);
    setAptResolved(true);
    setPinResolved(true);
    if (methods.length > 1) {
      setStep('method');
    } else {
      setStep('details');
      setFulfillmentMethod((methods[0] as FulfillmentMethod) ?? 'pickup');
    }
    // Only react to `isOpen`; everything else is resolved from the listing
    // prop at open time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const locale = i18n.language || 'en';
  const isDropoff = fulfillmentMethod === 'dropoff';

  // When the food changes hands: the picked time for pickup, the start of the
  // chef's run for drop-off (the same value handleConfirm sends).
  const handoffKey = isDropoff ? listing.dropoffWindow?.start ?? '' : pickupKey;

  // What the buyer ticked is "I know this is for Saturday", not "I know this
  // isn't today" — so a wheel spin that lands the pickup on a different DATE
  // (only possible across midnight) retracts it and asks again, as does
  // switching method. A spin that only moves the clock within the same day
  // leaves it alone.
  const handoffDate = dateOf(handoffKey);
  useEffect(() => {
    setDateAcknowledged(false);
  }, [handoffDate, fulfillmentMethod]);

  const unitPrice = listing.price ?? 0;
  const foodTotal = unitPrice * quantity;
  // Flat per ORDER, not per portion — the drive costs the same either way.
  const feeTotal = isDropoff ? listing.dropoffFee ?? 0 : 0;
  const grandTotal = foodTotal + feeTotal;

  const decrement = () => setQuantity((q) => Math.max(1, q - 1));
  const increment = () => setQuantity((q) => Math.min(maxQuantity, q + 1));

  // How far the chosen address sits from the listing. Measured against the
  // obfuscated coords (a 50-100m privacy offset, noise at mile granularity);
  // the authoritative check is the same test server-side on the real ones, so a
  // borderline address can still be refused at reserve.
  const listingPoint = parseObfuscated(listing.obfuscatedLocation);
  const addressPoint =
    orderAddress?.address.lat != null && orderAddress.address.lon != null
      ? { lat: orderAddress.address.lat, lon: orderAddress.address.lon }
      : null;
  const addressDistanceMi =
    listingPoint && addressPoint ? distanceMiles(listingPoint, addressPoint) : null;
  const addressOutOfRange =
    isDropoff &&
    listing.dropoffRadius != null &&
    addressDistanceMi != null &&
    addressDistanceMi > listing.dropoffRadius;

  // Drop-off needs somewhere to drive; pickup needs a time inside the window.
  const addressReady =
    !!orderAddress && aptResolved && pinResolved && !addressOutOfRange;
  // Resolved in the LISTING's zone, so "Today" means today where the food is
  // rather than on the buyer's phone. Same vocabulary the feed card uses
  // (Today / Tonight / Tomorrow / Saturday / April 12th), so the card they
  // tapped and this sheet can't name the day two different ways.
  const handoffMoment = formatPickupMoment(
    handoffKey,
    isDropoff ? listing.dropoffWindow?.timezone : pickupWindow?.timezone,
  );
  // Applies to drop-off too: a stated window like "Sat 5–6pm" is just as easy
  // to read as tonight's dinner as a bare clock time is.
  const needsDateAck = !!handoffMoment && !handoffMoment.isToday;
  const dateReady = !needsDateAck || dateAcknowledged;

  const canSubmit = isDropoff
    ? addressReady && !!listing.dropoffWindow?.start && !isSubmitting && dateReady
    : !!bounds && !!pickupKey && !isSubmitting && dateReady;

  const handleConfirm = () => {
    if (!canSubmit) return;
    if (isDropoff) {
      const start = listing.dropoffWindow?.start;
      if (!start) return;
      onConfirm({
        quantity,
        fulfillmentMethod,
        pickupTime: start,
        address: orderAddress?.address ?? null,
      });
      return;
    }
    if (!bounds || !pickupKey) return;
    onConfirm({ quantity, fulfillmentMethod, pickupTime: pickupKey });
  };

  const chooseMethod = (m: FulfillmentMethod) => {
    setFulfillmentMethod(m);
    setStep('details');
  };

  const methodCard = (m: FulfillmentMethod) => {
    const window = m === 'pickup' ? pickupWindow : listing.dropoffWindow;
    const tone = m === 'pickup' ? 'secondary' : 'tertiary';
    const Icon = m === 'pickup' ? Icons.pickup : Icons.dropoff;
    const fee = m === 'dropoff' ? listing.dropoffFee ?? 0 : 0;
    return (
      <button
        key={m}
        type="button"
        onClick={() => chooseMethod(m)}
        disabled={isSubmitting}
        className="flex w-full items-center gap-3 rounded-xl border border-border px-4 py-3 text-left active:bg-muted disabled:opacity-50"
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `var(--ion-color-${tone})`, color: '#000' }}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="font-medium text-foreground">
            {t(`orders:card.fulfillment.${m}` as const)}
          </span>
          <span className="text-sm text-muted-foreground">
            {formatTimeRange(window ?? null)}
            <span aria-hidden className="mx-1.5 opacity-40">·</span>
            {fee > 0
              ? t('portion.methodFee', { fee: formatPriceCents(fee) })
              : t('portion.methodFree')}
          </span>
        </span>
      </button>
    );
  };

  const quantityStepper = (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-foreground">
        {t('portion.quantityLabel')}
      </label>
      <div className="flex items-center justify-center gap-6">
        <button
          type="button"
          onClick={decrement}
          disabled={quantity <= 1 || isSubmitting}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-xl font-semibold active:bg-muted disabled:opacity-30"
        >
          −
        </button>
        <span className="min-w-[3rem] text-center text-3xl font-bold">{quantity}</span>
        <button
          type="button"
          onClick={increment}
          disabled={quantity >= maxQuantity || isSubmitting}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-xl font-semibold active:bg-muted disabled:opacity-30"
        >
          +
        </button>
      </div>
    </div>
  );

  // Not today: spell out how far off it is, then make them say so. The day
  // strip / window readout is passive — this is the part they can't walk past,
  // because Confirm stays locked until the box is ticked.
  const dateAck =
    needsDateAck && handoffMoment ? (
      <div className="space-y-2 pt-1">
        <div className="hp-pickup-alert">
          <CalendarClock className="hp-pickup-alert__icon h-5 w-5" aria-hidden="true" />
          <div>
            <p className="hp-pickup-alert__title">
              {t(isDropoff ? 'portion.notTodayTitleDropoff' : 'portion.notTodayTitle')}
            </p>
            <p>
              {t('portion.notTodayBody', {
                day: handoffMoment.dayLabel,
                // Floored at 1 so an already-closed window (reachable via a
                // share link) can't render "-3 days from now"; it falls to the
                // singular "not today" phrasing.
                count: Math.max(handoffMoment.daysAway, 1),
              })}
            </p>
          </div>
        </div>
        <button
          type="button"
          role="checkbox"
          aria-checked={dateAcknowledged}
          onClick={() => setDateAcknowledged((v) => !v)}
          disabled={isSubmitting}
          className={`hp-ack ${dateAcknowledged ? 'hp-ack--checked' : ''}`}
        >
          <span className="hp-ack__box" aria-hidden="true" />
          <span>{t('portion.notTodayAck', { day: handoffMoment.dayLabel })}</span>
        </button>
      </div>
    ) : null;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="auto-sheet">
      {/* Auto-height bottom sheet (see `.auto-sheet` in global.scss): grows to
          fit its content at any text size instead of a fixed breakpoint that
          clipped the controls at large text. Matters more now — the two steps
          are different heights. */}
      <div className="ion-page">
        <div className="auto-sheet-content px-6 pb-6 pt-4">
          <div className="space-y-6">
            <div className="relative">
              {/* Back only exists when there's a step to go back TO. */}
              {step === 'address' || (step === 'details' && methods.length > 1) ? (
                <button
                  type="button"
                  onClick={() => setStep(step === 'address' ? 'details' : 'method')}
                  disabled={isSubmitting}
                  aria-label={t('portion.back')}
                  className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground active:bg-muted disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              ) : null}
              {/* Symmetric px-10 keeps a long title centred without letting it
                  run under either corner button. */}
              <h2 className="px-10 text-center text-lg font-bold">{listing.title}</h2>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                aria-label={t('common:actions.close')}
                className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground active:bg-muted disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {step === 'address' ? (
              /* Order-scoped address entry. LocationPicker is controlled and
                 persists nothing on its own, which is exactly what's wanted:
                 the publish form uses the same component for a listing's
                 location and writes it to the listing, while here the value
                 goes on the ORDER and the profile is never touched. */
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-foreground">
                    {t('portion.dropoffAddressStepTitle')}
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {t('portion.dropoffAddressPerOrder')}
                  </p>
                </div>

                <LocationPicker
                  ref={locationPickerRef}
                  value={orderAddress}
                  // No profile seed and no geolocation probe: the suggestion
                  // rule lives in this component's open effect, and the picker
                  // reaching for the browser would go around it.
                  profileAddress={null}
                  autoSeed={false}
                  onAptResolutionChange={setAptResolved}
                  onPinResolutionChange={setPinResolved}
                  onChange={setOrderAddress}
                />

                {/* Stated here too, not just back on the details step: this is
                    where they're choosing, and letting them tap "Use this
                    address" only to find Confirm dead would be a worse way to
                    learn it. */}
                {addressOutOfRange ? (
                  <p className="text-sm text-brand-danger">
                    {t('portion.dropoffAddressOutOfRange', {
                      distance: addressDistanceMi?.toFixed(1),
                      radius: listing.dropoffRadius,
                    })}
                  </p>
                ) : null}

                <button
                  type="button"
                  onClick={() => setStep('details')}
                  disabled={!addressReady}
                  className="w-full rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
                >
                  {t('portion.dropoffAddressDone')}
                </button>
              </div>
            ) : step === 'method' ? (
              /* Step one. Both methods stated with their own window and cost so
                 the choice is made on the facts, not on a label. */
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('portion.methodStepTitle')}
                </label>
                <div className="space-y-2">
                  {offersPickup ? methodCard('pickup') : null}
                  {offersDropoff ? methodCard('dropoff') : null}
                </div>
              </div>
            ) : (
              <>
                {quantityStepper}

                {isDropoff ? (
                  /* Drop-off: no time to pick. The chef drives one run, so the
                     eater gets a window and needs to confirm where it lands. */
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-foreground">
                        {t('portion.dropoffArrivesLabel')}
                      </label>
                      <p className="text-base text-foreground">
                        {formatTimeRange(listing.dropoffWindow ?? null)}
                      </p>
                    </div>

                    {dateAck}

                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-foreground">
                        {t('portion.dropoffAddressLabel')}
                      </label>
                      {orderAddress ? (
                        <div className="flex items-start justify-between gap-2">
                          <p className="min-w-0 text-sm text-muted-foreground">
                            {orderAddress.displayText}
                          </p>
                          <button
                            type="button"
                            onClick={() => setStep('address')}
                            disabled={isSubmitting}
                            className="shrink-0 text-sm font-medium text-brand-primary underline decoration-dotted underline-offset-2 disabled:opacity-50"
                          >
                            {t('portion.dropoffAddressChange')}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2 rounded-xl border border-border p-3">
                          <p className="text-sm text-muted-foreground">
                            {t('portion.dropoffAddressNeeded')}
                          </p>
                          <button
                            type="button"
                            onClick={() => setStep('address')}
                            disabled={isSubmitting}
                            className="w-full rounded-lg border border-brand-primary py-2 text-sm font-medium text-brand-primary disabled:opacity-50"
                          >
                            {t('portion.dropoffAddressCta')}
                          </button>
                        </div>
                      )}
                      {/* An address can be chosen but still unusable: the picker
                          asks for an apt/unit and a pin confirmation, and an
                          unanswered prompt is what keeps Confirm disabled. */}
                      {orderAddress && !aptResolved ? (
                        <p className="text-sm text-brand-danger">
                          {t('portion.dropoffAddressConfirmApt')}
                        </p>
                      ) : null}
                      {orderAddress && !pinResolved ? (
                        <p className="text-sm text-brand-danger">
                          {t('portion.dropoffAddressConfirmPin')}
                        </p>
                      ) : null}
                      {addressOutOfRange ? (
                        <p className="text-sm text-brand-danger">
                          {t('portion.dropoffAddressOutOfRange', {
                            distance: addressDistanceMi?.toFixed(1),
                            radius: listing.dropoffRadius,
                          })}
                        </p>
                      ) : null}
                    </div>

                    {/* The fee is invisible everywhere else in checkout, so the
                        split is stated rather than folded silently into one
                        number. */}
                    {feeTotal > 0 ? (
                      <div className="space-y-1 border-t border-border pt-3 text-sm">
                        <div className="flex justify-between text-muted-foreground">
                          <span>{t('portion.totalFood')}</span>
                          <span>{formatPriceCents(foodTotal)}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>{t('portion.totalDelivery')}</span>
                          <span>{formatPriceCents(feeTotal)}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : bounds ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      {t('portion.pickupWhenLabel')}
                    </label>
                    {/* overflow-hidden so the dated strip's tinted background is
                        clipped by the box's own rounded corners. */}
                    <div className="flex flex-col items-center overflow-hidden rounded-xl border border-border">
                      {/* The day, stated above the wheel. The wheel itself can
                          only ever show a clock time, which is exactly how
                          buyers ended up reading a Saturday listing as
                          tonight's dinner. Future dates take the tinted variant
                          so today and not-today don't look alike. */}
                      {handoffMoment && (
                        <div
                          className={`hp-pickup-day ${
                            handoffMoment.isToday ? '' : 'hp-pickup-day--future'
                          }`}
                        >
                          <span className="hp-pickup-day__day">{handoffMoment.dayLabel}</span>
                          <span className="hp-pickup-day__time">{handoffMoment.time}</span>
                        </div>
                      )}
                      {/* min/max only for a same-day window. Across midnight there
                          is no time-of-day pair that describes the range (min
                          22:00 with max 01:00 leaves the wheel with no selectable
                          hours), so the wheel runs free and resolvePickup snaps
                          the picked time back into the window instead. */}
                      <IonDatetime
                        presentation="time"
                        value={withRefDate(timeOf(pickupKey))}
                        min={bounds.crossesMidnight ? undefined : withRefDate(timeOf(bounds.min))}
                        max={bounds.crossesMidnight ? undefined : withRefDate(timeOf(bounds.max))}
                        minuteValues={[0, 15, 30, 45]}
                        onIonChange={(e) => {
                          const picked = extractTime(e.detail.value);
                          if (picked) setPickupKey(resolvePickup(bounds, picked));
                        }}
                      />
                    </div>

                    {dateAck}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('portion.noWindow')}</p>
                )}

                <div className="space-y-1">
                  {/* Say why Confirm is locked, the same way checkout explains
                      its own disabled Pay button. The tick box is right above,
                      but a dead button with no stated reason reads as a bug. */}
                  {needsDateAck && !dateAcknowledged && !isSubmitting && (
                    <p className="pb-1 text-center text-sm text-muted-foreground">
                      {t('portion.lockedNeedsAck')}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!canSubmit}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <IonSpinner name="crescent" className="h-5 w-5" />
                        {t('portion.reserving')}
                      </>
                    ) : (
                      <span className="flex flex-col items-center leading-tight">
                        <span>
                          {t('portion.confirmWithTotal', {
                            amount: formatPriceCents(grandTotal),
                          })}
                        </span>
                        <span className="text-[11px] font-normal opacity-80">
                          {t('portion.plusTaxAndFees')}
                        </span>
                      </span>
                    )}
                  </button>

                  {/* Explicit way out, replacing the decorative grab handle. Not
                      labelled "Cancel": nothing has been ordered yet, and the
                      app's cancel copy elsewhere means cancelling a live order
                      or listing. */}
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="w-full py-3 text-center text-base font-medium text-muted-foreground active:opacity-60 disabled:opacity-50"
                  >
                    {t('portion.cancelCta')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </IonModal>
  );
};
